import { canUseLocalStorage, getWindow } from '@ps/web-core-utils';
import { TournamentState } from '@ps/web-poker-tile-mappers';
import type { ClubRecentTournamentSummary } from '@ps/web-shared-api-types/v1-preview/SwHomeGames';
import { produce } from 'immer';
import { get, isBoolean, isEmpty } from 'lodash';

import type { RootState, SelectorCreator, Slicer } from '@swf/core/store';

import normalizeWallPosts from '../components/Club/Wall/utils/normalizeWallPosts';
import { PKR_INITIAL_STATE, PKR_LOCAL_STORAGE } from '../constants';
import type { GamesByClubs, HGPokerGameTile, HomeGamesError, PageData } from '../types/common';
import {
  filterJoinedAndOwnedClubs,
  filterJoinedClubs,
  filterOwnedClubs
} from '../utils/filterClubs';

export default (slice: Slicer<PageData>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  clubs() {
    return slice((state) => state.clubs);
  },
  joinedClubs() {
    return slice((state) => state.clubs.filter(filterJoinedClubs));
  },
  ownedClubs() {
    return slice((state) => state.clubs.filter(filterOwnedClubs));
  },
  joinedOrOwnedClubsIds() {
    return slice((state) =>
      state.clubs.filter(filterJoinedAndOwnedClubs).map((club) => club.clubId)
    );
  },
  tournaments() {
    return slice((state) => state.games.tourns);
  },
  selectedTournamentServIntFlag() {
    return slice((state) => {
      const tourn =
        state.games.tourns && state.games.selectedGameIds
          ? state.games.tourns.find(
              ({ staticData }) =>
                staticData && staticData.tournamentId === state.games.selectedGameIds[0]
            )
          : null;

      return tourn?.staticData?.tournFlagsServInt;
    });
  },
  wallPosts() {
    return slice((state) => normalizeWallPosts(state.wallPosts).reverse());
  },
  announcement() {
    return slice((state) => state.announcement);
  },
  isTournsActive() {
    return slice((state) => {
      const isTournsActive =
        state.serviceStatusData.length > 0
          ? state.serviceStatusData.some(
              ({ serverObject, status }) =>
                serverObject && serverObject === 'LobbyServerInstance' && status === 'AVAILABLE'
            )
          : false;
      return isTournsActive;
    });
  },
  isCashGameActive() {
    return slice((state) => {
      const isCashGameActive =
        state.serviceStatusData.length > 0
          ? state.serviceStatusData.some(
              ({ serverObject, status }) =>
                serverObject &&
                serverObject === 'RingGamesLobbyServerInstance' &&
                status === 'AVAILABLE'
            )
          : false;
      return isCashGameActive;
    });
  },
  tables() {
    return slice((state) => state.games.tables);
  },
  scheduledTables() {
    return slice((state) =>
      state.games.tables?.map((table) => ({
        ...table,
        ...state.games.scheduledTables?.find(
          (scheduledTable) => scheduledTable.tableId === table.tableId
        )
      }))
    );
  },
  recentTourns() {
    return slice((state) => state.games.recentTourns);
  },
  registeredTourns() {
    return slice((state) => state.games.registeredTourns);
  },
  playerTournamentResults() {
    return slice((state) => state.playerTournamentResults);
  },
  selectedGames() {
    return slice((state) => state.games.selectedGameIds);
  },
  loading() {
    return slice((state) => state.loading);
  },
  playersByClubId() {
    return slice((state) => state.playersByClubId);
  },
  tournamentStructures() {
    return slice((state) => state.tournamentStructures);
  },
  tableStructures() {
    return slice((state) => state.tableStructures);
  },
  clubMembers() {
    return slice((state) => state.clubMembers);
  },
  clubSeasons() {
    return createSelector(
      slice,
      (_: RootState, clubId: string) => clubId,
      ({ clubSeasonsById }, clubId) =>
        produce(clubSeasonsById[clubId], (draft = []) => {
          draft.sort((a, b) =>
            a.timeFrom && b.timeFrom
              ? new Date(b.timeFrom).getTime() - new Date(a.timeFrom).getTime()
              : 0
          );
        })
    );
  },
  localStorageItem() {
    return createSelector(
      slice,
      (_: RootState, path: string) => path,
      ({ localStorage }, path) => {
        if (!canUseLocalStorage) return;

        const ls = getWindow()?.localStorage.getItem(PKR_LOCAL_STORAGE);
        const item = get(localStorage, path);

        if (!ls) {
          getWindow()?.localStorage.setItem(
            PKR_LOCAL_STORAGE,
            JSON.stringify(PKR_INITIAL_STATE.localStorage)
          );
        }

        try {
          if (isBoolean(item)) return item;
          if (!isEmpty(item)) return item;

          const lsItem = get(JSON.parse(ls ?? '{}'), path);

          if (isBoolean(lsItem)) return lsItem;
          if (!isEmpty(lsItem)) return lsItem;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          return;
        }
      }
    );
  },
  error() {
    return createSelector(
      slice,
      (_: RootState, level: keyof HomeGamesError) => level,
      ({ error }, level) => error[level]
    );
  },
  clubSeasonStats() {
    return createSelector(
      slice,
      (_: RootState, clubId: string) => clubId,
      ({ clubSeasonStatsById }, clubId) => clubSeasonStatsById[clubId]
    );
  },
  clubSeasonRanks() {
    return slice((state) => state.wallRanks);
  },
  clubSummaries() {
    return slice((state) => state.clubRecentTournamentSummary);
  },
  clubTournamentSummary() {
    return createSelector(
      slice,
      (_: RootState, tournId: number) => tournId,
      ({ clubRecentTournamentSummary }, tournId) =>
        clubRecentTournamentSummary.find((summary) => Number(summary.tournId) === tournId)
    );
  },
  clubSummariesByDate() {
    return slice((state) =>
      state.clubRecentTournamentSummary.reduce(
        (acc: Record<string, ClubRecentTournamentSummary[]>, summary) => {
          const { started } = summary;
          const date = started?.split('T')[0];

          if (date) {
            if (!acc[date]) {
              acc[date] = [];
            }

            acc[date].push(summary);
          }

          return acc;
        },
        {}
      )
    );
  },
  clubOptionsById() {
    return slice((state) => state.clubOptionsById);
  },
  getScheduledTournTiles() {
    return createSelector(
      slice,
      (rootState: RootState, tournaments: GamesByClubs) => tournaments,
      ({ games: { registeredTourns } }, tournaments) =>
        produce(tournaments, (draft) => {
          const noGames = isEmpty(tournaments);
          const noRegistered = isEmpty(registeredTourns);

          if (noRegistered && !noGames) return tournaments;
          if (noRegistered && noGames) return {};

          registeredTourns?.forEach(({ clubId, tournId }) => {
            const index = draft[Number(clubId)]?.findIndex(
              (tourn) => Number(tourn.id) === Number(tournId)
            );
            if (index > -1) {
              draft[Number(clubId)] = draft[Number(clubId)].filter((_, i) => i !== index);
            }
          });
        })
    );
  },
  getRegisteredTournTiles() {
    return createSelector(
      slice,
      (rootState: RootState, tournaments: GamesByClubs) => tournaments,
      ({ games: { registeredTourns } }, tournaments) => {
        const registeredTiles: Record<string, HGPokerGameTile[]> = {};
        registeredTourns?.forEach(({ tournId, clubId }) => {
          if (!clubId) {
            return;
          }

          const match = tournaments[clubId]?.find(
            (tournament) => tournId?.toString() === tournament.id
          );

          if (!registeredTiles[clubId]) {
            registeredTiles[clubId] = [];
          }

          if (match) {
            return (registeredTiles[clubId] = [...registeredTiles[clubId], match]);
          }
        });
        return registeredTiles;
      }
    );
  },
  getTournamentRegistrationStatus() {
    return createSelector(
      slice,
      (_: RootState, props: { id: string | number; tournamentState: TournamentState }) => props,
      ({ games: { scheduledTournaments } }, { id, tournamentState }) => {
        const tournament = scheduledTournaments?.find(
          ({ tournId }) => Number(tournId) === Number(id)
        );

        return {
          show:
            !tournament?.cancelled &&
            tournamentState !== TournamentState.CANCELLED &&
            tournamentState !== TournamentState.COMPLETED &&
            tournamentState !== TournamentState.FULL &&
            tournamentState !== TournamentState.RESTRICTED,
          eligible: tournament?.eligibleToRegister,
          registered: tournament?.registered
        };
      }
    );
  },
  hasGames() {
    return slice(({ games: { tables, tourns } }) => !!(tables?.length || tourns?.length));
  },
  uiLayout() {
    return slice((state) => state.uiLayout);
  }
});
